import { useCallback, useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import {
  formatMeters,
  formatSeconds,
  haversineMeters,
  parseCoord,
} from '../utils/deliveryGeo'
import './DeliveryMapPanel.css'

// Vite / bundlers break default Leaflet marker icons — restore (same visual as legacy)
const DefaultIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowSize: [41, 41],
})
L.Marker.prototype.options.icon = DefaultIcon

type PickMode = 'A' | 'B' | null

export interface DeliveryMapPanelProps {
  latA: string
  lngA: string
  latB: string
  lngB: string
  speed: number
  onLatLngChange: (point: 'A' | 'B', lat: number, lng: number) => void
  pickMode: PickMode
  onPickModeChange: (mode: PickMode) => void
  /** Clear A/B fields in parent (same as legacy «נקה»). */
  onResetAll: () => void
}

export default function DeliveryMapPanel({
  latA,
  lngA,
  latB,
  lngB,
  speed,
  onLatLngChange,
  pickMode,
  onPickModeChange,
  onResetAll,
}: DeliveryMapPanelProps) {
  const mapElRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const aMarkerRef = useRef<L.Marker | null>(null)
  const bMarkerRef = useRef<L.Marker | null>(null)
  const pathRef = useRef<L.Polyline | null>(null)
  const droneRef = useRef<L.CircleMarker | null>(null)
  const animFrameRef = useRef<number | null>(null)

  const [distanceLabel, setDistanceLabel] = useState('—')
  const [etaLabel, setEtaLabel] = useState('—')
  const [status, setStatus] = useState('מוכן')
  const [logLines, setLogLines] = useState<string[]>([])

  const log = useCallback((message: string) => {
    const time = new Date().toLocaleTimeString('he-IL')
    setLogLines((prev) => [...prev.slice(-80), `[${time}] ${message}`])
  }, [])

  const drawPathIfReady = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const la = parseCoord(latA)
    const lnA = parseCoord(lngA)
    const lb = parseCoord(latB)
    const lnB = parseCoord(lngB)
    if (la === null || lnA === null || lb === null || lnB === null) {
      return
    }
    const latlngs: L.LatLngExpression[] = [
      [la, lnA],
      [lb, lnB],
    ]
    if (!pathRef.current) {
      pathRef.current = L.polyline(latlngs, { color: '#3bd0ff' }).addTo(map)
    } else {
      pathRef.current.setLatLngs(latlngs)
    }
    const dist = haversineMeters(la, lnA, lb, lnB)
    setDistanceLabel(formatMeters(dist))
    const seconds = dist / Math.max(0.5, speed)
    setEtaLabel(formatSeconds(seconds))
    const b = pathRef.current.getBounds()
    map.fitBounds(b, { padding: [30, 30] })
  }, [latA, lngA, latB, lngB, speed])

  // Init map once: default = satellite (Esri World Imagery); optional street map layer
  useEffect(() => {
    if (!mapElRef.current || mapRef.current) return
    const map = L.map(mapElRef.current).setView([31.771959, 35.217018], 12)
    const satellite = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        maxZoom: 19,
        attribution:
          'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      }
    )
    const streets = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors',
    })
    satellite.addTo(map)
    L.control
      .layers(
        {
          'לווין': satellite,
          'מפת רחובות': streets,
        },
        {},
        { position: 'topright' }
      )
      .addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const handler = (e: L.LeafletMouseEvent) => {
      if (!pickMode) return
      const { lat, lng } = e.latlng
      onLatLngChange(pickMode, lat, lng)
      onPickModeChange(null)
      document.body.style.cursor = 'default'
    }
    map.on('click', handler)
    return () => {
      map.off('click', handler)
    }
  }, [pickMode, onLatLngChange, onPickModeChange])

  useEffect(() => {
    document.body.style.cursor = pickMode ? 'crosshair' : 'default'
    return () => {
      document.body.style.cursor = 'default'
    }
  }, [pickMode])

  // Markers + path sync
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const la = parseCoord(latA)
    const lnA = parseCoord(lngA)
    const lb = parseCoord(latB)
    const lnB = parseCoord(lngB)

    if (la !== null && lnA !== null) {
      if (!aMarkerRef.current) {
        aMarkerRef.current = L.marker([la, lnA], { draggable: true }).addTo(map)
        aMarkerRef.current.on('dragend', () => {
          const p = aMarkerRef.current?.getLatLng()
          if (p) onLatLngChange('A', p.lat, p.lng)
        })
      } else {
        aMarkerRef.current.setLatLng([la, lnA])
      }
    }
    if (lb !== null && lnB !== null) {
      if (!bMarkerRef.current) {
        bMarkerRef.current = L.marker([lb, lnB], { draggable: true }).addTo(map)
        bMarkerRef.current.on('dragend', () => {
          const p = bMarkerRef.current?.getLatLng()
          if (p) onLatLngChange('B', p.lat, p.lng)
        })
      } else {
        bMarkerRef.current.setLatLng([lb, lnB])
      }
    }
    drawPathIfReady()
  }, [latA, lngA, latB, lngB, drawPathIfReady, onLatLngChange])

  const animateDrone = () => {
    const path = pathRef.current
    const map = mapRef.current
    if (!path || !map) return
    const latlngs = path.getLatLngs() as L.LatLng[]
    if (latlngs.length < 2) return
    const start = latlngs[0]
    const end = latlngs[latlngs.length - 1]
    const dist = map.distance(start, end)
    const duration = Math.max(1, dist / Math.max(0.5, speed))
    const startTime = performance.now()
    setStatus('בדרך')
    log('הרחפן יצא לדרך.')

    const step = (t: number) => {
      const p = Math.min(1, (t - startTime) / (duration * 1000))
      const lat = start.lat + (end.lat - start.lat) * p
      const lng = start.lng + (end.lng - start.lng) * p
      if (!droneRef.current) {
        droneRef.current = L.circleMarker([lat, lng], {
          radius: 6,
          color: '#72f1b8',
          weight: 3,
        }).addTo(map)
      } else {
        droneRef.current.setLatLng([lat, lng])
      }
      if (p < 1) {
        animFrameRef.current = requestAnimationFrame(step)
      } else {
        setStatus('המשלוח הושלם')
        log('הרחפן הגיע ליעד.')
      }
    }
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    animFrameRef.current = requestAnimationFrame(step)
  }

  const resetMap = () => {
    onResetAll()
    setDistanceLabel('—')
    setEtaLabel('—')
    setStatus('מוכן')
    const map = mapRef.current
    if (pathRef.current && map) {
      map.removeLayer(pathRef.current)
      pathRef.current = null
    }
    if (droneRef.current && map) {
      map.removeLayer(droneRef.current)
      droneRef.current = null
    }
    if (aMarkerRef.current && map) {
      map.removeLayer(aMarkerRef.current)
      aMarkerRef.current = null
    }
    if (bMarkerRef.current && map) {
      map.removeLayer(bMarkerRef.current)
      bMarkerRef.current = null
    }
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
  }

  // Expose reset to parent via clearing coords — parent passes '' and we need handle NaN trick
  // Simpler: parent calls setLatA('') etc; parseCoord returns null and markers removed in another effect
  useEffect(() => {
    const la = parseCoord(latA)
    const lnA = parseCoord(lngA)
    const lb = parseCoord(latB)
    const lnB = parseCoord(lngB)
    const map = mapRef.current
    if (la === null || lnA === null) {
      if (aMarkerRef.current && map) {
        map.removeLayer(aMarkerRef.current)
        aMarkerRef.current = null
      }
    }
    if (lb === null || lnB === null) {
      if (bMarkerRef.current && map) {
        map.removeLayer(bMarkerRef.current)
        bMarkerRef.current = null
      }
    }
    if (
      (la === null || lnA === null || lb === null || lnB === null) &&
      pathRef.current &&
      map
    ) {
      map.removeLayer(pathRef.current)
      pathRef.current = null
      setDistanceLabel('—')
      setEtaLabel('—')
    }
  }, [latA, lngA, latB, lngB])

  return (
    <div className="delivery-map-layout">
      <div className="delivery-map-wrap">
        <div ref={mapElRef} className="delivery-map-el" />
      </div>
      <div className="delivery-map-side panel-side">
        <div className="status-list">
          <div className="status-item">
            <div className="name">מרחק</div>
            <div className="value">{distanceLabel}</div>
          </div>
          <div className="status-item">
            <div className="name">זמן משוער</div>
            <div className="value">{etaLabel}</div>
          </div>
          <div className="status-item">
            <div className="name">סטטוס</div>
            <div className="value">{status}</div>
          </div>
        </div>
        <div className="delivery-map-actions">
          <button type="button" className="btn-secondary" onClick={animateDrone}>
            סימולציית מסלול (הרחפן)
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              resetMap()
            }}
          >
            נקה מפה
          </button>
        </div>
        <div className="log delivery-log">{logLines.join('\n')}</div>
      </div>
    </div>
  )
}
